import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Cpu, Radio, Wifi, Database, BarChart3, Globe, Building2, CheckCircle2, ArrowRight } from 'lucide-react';

const IoTSmartCity = () => {
  const solutions = [
    {
      icon: Cpu,
      title: "IoT Infrastructure",
      description: "End-to-end IoT platform development, sensor integration, and device management for smart environments."
    },
    {
      icon: Building2,
      title: "Smart City Platforms",
      description: "Integrated smart city solutions including traffic management, waste management, and energy optimization."
    },
    {
      icon: Radio,
      title: "Connected Devices",
      description: "Industrial IoT devices, smart sensors, and gateway solutions for seamless connectivity."
    },
    {
      icon: BarChart3,
      title: "Data Analytics",
      description: "Real-time data analytics, predictive maintenance, and AI-powered insights for smart decision making."
    },
    {
      icon: Wifi,
      title: "Wireless Connectivity",
      description: "LoRaWAN, NB-IoT, and 5G connectivity solutions for wide-area IoT deployments."
    },
    {
      icon: Globe,
      title: "Urban Transformation",
      description: "Complete digital transformation for cities including smart lighting, parking, and public safety systems."
    }
  ];

  const benefits = [
    "Reduced operational costs",
    "Improved resource efficiency",
    "Enhanced citizen services",
    "Real-time monitoring",
    "Predictive maintenance",
    "Data-driven decisions"
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>IoT & Smart City Solutions | CNGTPL</title>
        <meta name="description" content="Comprehensive IoT infrastructure and smart city solutions for urban transformation and digital connectivity." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="bg-blue-100 text-blue-700 mb-6">Smart Infrastructure</Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            IoT & <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Smart City</span> Solutions
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Transform cities into intelligent ecosystems with our comprehensive IoT infrastructure, smart sensors, and data analytics platforms.
          </p>
          <Link to="/contact">
            <Button className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-8 py-6 text-lg">
              Get Started
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our IoT & Smart City Solutions</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Comprehensive technology solutions for urban digital transformation</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <Card key={index} className="p-8 hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-200">
                <solution.icon className="w-12 h-12 text-blue-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">{solution.title}</h3>
                <p className="text-gray-600">{solution.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Key Benefits</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center space-x-3 bg-white p-6 rounded-lg shadow-md">
                <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-cyan-500 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">Ready to Build Smart Infrastructure?</h2>
          <p className="text-xl mb-8 opacity-90">Let's discuss how IoT can transform your city or organization</p>
          <Link to="/contact">
            <Button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg">
              Contact Our IoT Experts
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default IoTSmartCity;